const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

export const WORKFLOW_STAGES = [
  { key: 'request', fr: 'Demande', en: 'Request' },
  { key: 'match', fr: 'Artisan', en: 'Artisan' },
  { key: 'assessment', fr: 'Visite', en: 'Assessment' },
  { key: 'quote', fr: 'Devis', en: 'Quote' },
  { key: 'deposit', fr: 'Acompte', en: 'Deposit' },
  { key: 'work', fr: 'Chantier', en: 'Work' },
  { key: 'validation', fr: 'Validation', en: 'Validation' },
  { key: 'close', fr: 'Clôture', en: 'Close' },
];

export const projectDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day, 9, 0, 0, 0);
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const phrase = (fr, en) => ({ fr, en });

export const remainingTime = (target, now = Date.now()) => {
  const date = projectDate(target);
  if (!date) return null;
  const diff = date.getTime() - Number(now || Date.now());
  const abs = Math.abs(diff);
  const future = diff >= 0;
  if (abs < HOUR_MS) {
    const minutes = Math.max(1, Math.round(abs / 60000));
    return phrase(
      future ? `dans ${minutes} min` : `il y a ${minutes} min`,
      future ? `in ${minutes} min` : `${minutes} min ago`,
    );
  }
  if (abs < DAY_MS) {
    const hours = Math.max(1, Math.round(abs / HOUR_MS));
    return phrase(
      future ? `dans ${hours} h` : `il y a ${hours} h`,
      future ? `in ${hours} hr` : `${hours} hr ago`,
    );
  }
  const days = Math.max(1, Math.ceil(abs / DAY_MS));
  return phrase(
    future ? `dans ${days} j` : `il y a ${days} j`,
    future ? `in ${days} d` : `${days} d ago`,
  );
};

const paymentState = (demand = {}) => {
  const depositPaid = demand.payments?.deposit?.status === 'paid' || Boolean(demand.deposit_paid_at);
  const finalPaid = demand.payments?.final?.status === 'paid' || Boolean(demand.paid_at) || ['paid', 'reviewed'].includes(demand.demand_status);
  return { depositPaid, finalPaid };
};

const make = ({
  stageIndex,
  headline,
  detail,
  nextAction,
  owner = 'system',
  target = null,
  targetLabel = null,
  tone = 'neutral',
  complete = false,
}) => ({
  stageIndex,
  progress: complete ? 100 : Math.max(4, Math.min(96, Math.round(((stageIndex + 0.35) / WORKFLOW_STAGES.length) * 100))),
  headline,
  detail,
  nextAction,
  owner,
  target,
  targetLabel,
  tone,
  complete,
});

export const getProjectWorkflow = (demand = {}, role = 'Client', now = Date.now()) => {
  const status = String(demand.demand_status || 'unassigned');
  const { depositPaid, finalPaid } = paymentState(demand);
  const assessmentMode = demand.assessment_workflow?.mode || demand.estimation?.assessment_mode || 'visit_required';
  const artisanVisitConfirmed = Boolean(demand.visit_confirmation?.artisan_confirmed_at);
  const arrivalConfirmed = Boolean(demand.arrival_confirmation?.confirmed);
  const visitDate = projectDate(demand.estimation?.visit_date || demand.visit_confirmation?.scheduled_for);
  const startDate = projectDate(demand.contract?.start_date);
  const endDate = projectDate(demand.contract?.end_date);
  const isClient = role === 'Client';

  if (status === 'reviewed') {
    return make({
      stageIndex: 7,
      complete: true,
      tone: 'success',
      headline: phrase('Projet clôturé', 'Project closed'),
      detail: phrase('Travaux, paiement et avis sont terminés.', 'Work, payment and reviews are complete.'),
      nextAction: phrase('Aucune action requise.', 'No action required.'),
    });
  }

  if (status === 'paid' || finalPaid) {
    return make({
      stageIndex: 7,
      tone: 'success',
      headline: phrase('Paiement confirmé', 'Payment confirmed'),
      detail: phrase('Le chantier est payé et sécurisé.', 'The job is paid and secured.'),
      nextAction: isClient
        ? phrase('Partagez votre avis pour clôturer le projet.', 'Share your review to close the project.')
        : phrase('Ajoutez le chantier à votre portfolio et évaluez le client.', 'Add the job to your portfolio and review the client.'),
      owner: isClient ? 'client' : 'artisan',
    });
  }

  if (status === 'finished') {
    return make({
      stageIndex: 7,
      tone: 'attention',
      headline: phrase('Travaux validés', 'Work approved'),
      detail: phrase('La fin des travaux a été confirmée.', 'Completion has been confirmed.'),
      nextAction: phrase('Le client règle maintenant le solde sécurisé.', 'The client now pays the secured final balance.'),
      owner: 'client',
    });
  }

  if (status === 'completion_disputed') {
    return make({
      stageIndex: 6,
      tone: 'attention',
      headline: phrase('Point à résoudre', 'Issue to resolve'),
      detail: phrase('Un point a été signalé avant le paiement du solde.', 'An issue was reported before the final payment.'),
      nextAction: phrase('L’artisan corrige ou répond, puis redemande la validation.', 'The artisan resolves it and requests validation again.'),
      owner: 'artisan',
    });
  }

  if (status === 'completion_pending') {
    return make({
      stageIndex: 6,
      tone: 'attention',
      headline: phrase('Travaux déclarés terminés', 'Work declared complete'),
      detail: phrase('Le chantier attend la vérification du client.', 'The job is waiting for the client’s check.'),
      nextAction: phrase('Le client confirme la fin des travaux ou signale un problème.', 'The client confirms completion or reports an issue.'),
      owner: 'client',
    });
  }

  if (status === 'assigned') {
    if (!depositPaid) {
      return make({
        stageIndex: 4,
        tone: 'attention',
        headline: phrase('Devis accepté · acompte attendu', 'Quote accepted · deposit pending'),
        detail: phrase('Le chantier n’est pas autorisé à démarrer avant confirmation de l’acompte.', 'The job must not start before the deposit is confirmed.'),
        nextAction: phrase('Le client règle l’acompte sécurisé.', 'The client pays the secured deposit.'),
        owner: 'client',
      });
    }
    if (arrivalConfirmed) {
      return make({
        stageIndex: 5,
        tone: 'success',
        headline: phrase('Artisan arrivé sur le chantier', 'Artisan arrived on site'),
        detail: phrase('L’arrivée a été confirmée depuis la zone du chantier.', 'Arrival was confirmed from the job area.'),
        nextAction: phrase('Travaux en cours selon le devis accepté.', 'Work is underway under the accepted quote.'),
        owner: 'artisan',
        target: endDate,
        targetLabel: endDate ? phrase('Fin prévue', 'Planned finish') : null,
      });
    }
    if (startDate && startDate.getTime() > now) {
      return make({
        stageIndex: 5,
        tone: 'progress',
        headline: phrase('Chantier planifié', 'Job scheduled'),
        detail: phrase('Acompte confirmé. Le démarrage est programmé.', 'Deposit confirmed. The start is scheduled.'),
        nextAction: phrase('L’artisan se prépare pour le démarrage du chantier.', 'The artisan is preparing for the scheduled start.'),
        owner: 'artisan',
        target: startDate,
        targetLabel: phrase('Démarrage', 'Starts'),
      });
    }
    return make({
      stageIndex: 5,
      tone: 'progress',
      headline: phrase('Chantier prêt à démarrer', 'Job ready to start'),
      detail: phrase('Acompte confirmé. Le chantier peut commencer.', 'Deposit confirmed. The job can start.'),
      nextAction: phrase('L’artisan réalise les travaux puis déclare leur fin.', 'The artisan carries out the work and declares completion.'),
      owner: 'artisan',
      target: endDate,
      targetLabel: endDate ? phrase('Fin prévue', 'Planned finish') : null,
    });
  }

  if (status === 'modification_asked') {
    return make({
      stageIndex: 3,
      tone: 'attention',
      headline: phrase('Révision du devis', 'Quote revision'),
      detail: phrase('Le client a demandé une modification du devis.', 'The client requested a quote revision.'),
      nextAction: phrase('L’artisan modifie et renvoie une nouvelle version.', 'The artisan revises and resends the quote.'),
      owner: 'artisan',
    });
  }

  if (status === 'contract_offered') {
    return make({
      stageIndex: 3,
      tone: 'attention',
      headline: phrase('Devis prêt à décider', 'Quote ready for decision'),
      detail: phrase('Le devis détaillé a été transmis au client.', 'The detailed quote was sent to the client.'),
      nextAction: phrase('Le client accepte le devis ou demande une modification.', 'The client accepts the quote or asks for changes.'),
      owner: 'client',
    });
  }

  if (status === 'estimation_visit_valid') {
    return make({
      stageIndex: 3,
      tone: 'progress',
      headline: phrase('Préparation du devis détaillé', 'Detailed quote in preparation'),
      detail: phrase('Les informations nécessaires sont validées.', 'The required information has been validated.'),
      nextAction: phrase('L’artisan prépare le devis complet avec coûts et planning.', 'The artisan prepares the full quote with costs and schedule.'),
      owner: 'artisan',
    });
  }

  if (status === 'estimation_confirmed') {
    if (assessmentMode === 'visit_required') {
      if (artisanVisitConfirmed) {
        return make({
          stageIndex: 2,
          tone: 'attention',
          headline: phrase('Visite effectuée', 'Visit completed'),
          detail: phrase('L’artisan a confirmé la visite.', 'The artisan confirmed the visit.'),
          nextAction: phrase('Le client confirme la visite pour passer au devis détaillé.', 'The client confirms the visit to move to the detailed quote.'),
          owner: 'client',
        });
      }
      return make({
        stageIndex: 2,
        tone: 'progress',
        headline: phrase('Visite chantier planifiée', 'Site visit scheduled'),
        detail: phrase('Le projet attend la visite ou sa confirmation par l’artisan.', 'The project is waiting for the site visit or the artisan’s confirmation.'),
        nextAction: phrase('L’artisan effectue la visite et confirme son passage.', 'The artisan performs the visit and confirms it.'),
        owner: 'artisan',
        target: visitDate,
        targetLabel: visitDate ? phrase('Visite', 'Visit') : null,
      });
    }
    return make({
      stageIndex: 2,
      tone: 'progress',
      headline: phrase('Informations complémentaires', 'Additional information'),
      detail: phrase('L’artisan vérifie les informations nécessaires au chiffrage.', 'The artisan is checking the information needed for pricing.'),
      nextAction: phrase('L’artisan valide les informations puis prépare le devis.', 'The artisan validates the information and prepares the quote.'),
      owner: 'artisan',
    });
  }

  if (status === 'estimation_wait' || (status === 'unassigned' && Number(demand.offer_count || 0) > 0)) {
    const count = Math.max(1, Number(demand.offer_count || 1));
    return make({
      stageIndex: 1,
      tone: 'attention',
      headline: phrase(
        `${count} proposition${count > 1 ? 's' : ''} reçue${count > 1 ? 's' : ''}`,
        `${count} proposal${count > 1 ? 's' : ''} received`,
      ),
      detail: phrase('Le projet attend le choix du client.', 'The project is waiting for the client’s choice.'),
      nextAction: phrase('Le client compare les propositions et sélectionne un artisan.', 'The client compares proposals and selects an artisan.'),
      owner: 'client',
    });
  }

  return make({
    stageIndex: 0,
    tone: 'neutral',
    headline: phrase('Recherche d’un artisan', 'Finding an artisan'),
    detail: phrase('La demande est ouverte aux artisans compatibles.', 'The request is open to matching artisans.'),
    nextAction: phrase('Les artisans peuvent consulter la demande et proposer une première estimation.', 'Artisans can review the request and send an initial proposal.'),
    owner: 'artisan',
  });
};

export const workflowText = (value, language = 'fr') => value?.[language === 'en' ? 'en' : 'fr'] || value?.fr || value?.en || '';

export const ownerLabel = (owner, role, language = 'fr') => {
  const en = language === 'en';
  if (owner === 'client') return role === 'Client' ? (en ? 'Your action' : 'À vous') : (en ? 'Client' : 'Client');
  if (owner === 'artisan') return role === 'Artisan' ? (en ? 'Your action' : 'À vous') : (en ? 'Artisan' : 'Artisan');
  return en ? 'Automatic' : 'Automatique';
};
